// src/pages/Profile/ProfilePage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Box,
  Button,
  TextField,
  Divider,
  Chip,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Verified,
  Star,
  TrendingUp,
  Business,
  People,
  Lightbulb,
  Comment,
  ContactMail,
  Assignment,
  Upgrade,
  CheckCircle,
  Lock,
  Info,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import api from '../../services/api';

const ProfilePage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [usageLimits, setUsageLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsageLimits();
  }, []);

  const fetchUsageLimits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/profile/');
      setUsageLimits(response.usage_limits);
    } catch (error) {
      console.error('Error fetching usage limits:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used, total) => {
    if (total === 'unlimited') return 0;
    return (used / total) * 100;
  };

  const getProgressColor = (percentage) => {
    if (percentage < 50) return 'success';
    if (percentage < 80) return 'warning';
    return 'error';
  };

  const UsageStat = ({ icon: Icon, label, used, total, color }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 20, color }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color }}>
          {total === 'unlimited' ? (
            <Chip label="Unlimited" size="small" sx={{ bgcolor: '#10B98120', color: '#10B981' }} />
          ) : (
            `${used} / ${total}`
          )}
        </Typography>
      </Box>
      {total !== 'unlimited' && (
        <LinearProgress
          variant="determinate"
          value={getUsagePercentage(used, total)}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: '#E5E7EB',
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
            },
          }}
        />
      )}
    </Box>
  );

  const getPlanName = () => {
    if (user?.is_premium) {
      return 'Premium Plan';
    }
    return 'Free Plan';
  };

  const getPlanColor = () => {
    if (user?.is_premium) {
      return '#10B981';
    }
    return '#F59E0B';
  };

  const getPlanBadge = () => {
    if (user?.is_premium) {
      return <Verified sx={{ fontSize: 18, color: '#10B981' }} />;
    }
    return <Lock sx={{ fontSize: 18, color: '#F59E0B' }} />;
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading profile...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar
                src={user?.profile_picture}
                sx={{ width: 100, height: 100, mr: 3, border: '3px solid #2E7D32' }}
              >
                {user?.full_name?.[0]}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {user?.full_name}
                  </Typography>
                  {user?.is_verified && (
                    <Tooltip title="Verified Account">
                      <Verified sx={{ color: '#2E7D32', fontSize: 20 }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography color="text.secondary">{user?.email}</Typography>
                <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
                  Member since {new Date(user?.date_joined).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={user?.first_name || ''}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={user?.last_name || ''}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user?.email || ''}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={user?.phone_number || 'Not provided'}
                  disabled
                  size="small"
                />
              </Grid>
              {user?.company_name && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company"
                    value={user?.company_name}
                    disabled
                    size="small"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="User Type"
                  value={user?.user_type === 'organization' ? 'Organization' : 'Supplier/Vendor'}
                  disabled
                  size="small"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="outlined" disabled>
                Edit Profile (Coming Soon)
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Subscription & Usage Card */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getPlanBadge()}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {getPlanName()}
                </Typography>
              </Box>
              {!user?.is_premium && (
                <Button
                  variant="contained"
                  startIcon={<Upgrade />}
                  onClick={() => setUpgradeDialogOpen(true)}
                  sx={{
                    bgcolor: '#2E7D32',
                    '&:hover': { bgcolor: '#1B5E20' },
                    borderRadius: '20px',
                    textTransform: 'none',
                  }}
                >
                  Upgrade to Premium
                </Button>
              )}
            </Box>

            {user?.is_premium && user?.premium_expiry_date && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Premium expires on {new Date(user.premium_expiry_date).toLocaleDateString()}
              </Alert>
            )}

            {/* Usage Limits Section */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#2E7D32' }}>
              Usage Limits
            </Typography>

            {user?.user_type === 'organization' && usageLimits && (
              <>
                <UsageStat
                  icon={Assignment}
                  label="Problems Posted"
                  used={usageLimits.problems_posted}
                  total={usageLimits.problems_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.problems_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
                <UsageStat
                  icon={Lightbulb}
                  label="Solutions Viewed"
                  used={usageLimits.solutions_viewed}
                  total={usageLimits.solutions_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.solutions_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
              </>
            )}

            {user?.user_type === 'supplier' && usageLimits && (
              <>
                <UsageStat
                  icon={Assignment}
                  label="Problems Viewed"
                  used={usageLimits.problems_viewed}
                  total={usageLimits.problems_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.problems_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
                <UsageStat
                  icon={Comment}
                  label="Comments Posted (Monthly)"
                  used={usageLimits.comments_posted}
                  total={usageLimits.comments_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.comments_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
                <UsageStat
                  icon={ContactMail}
                  label="Contacts Made (Monthly)"
                  used={usageLimits.contacts_made}
                  total={usageLimits.contacts_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.contacts_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
                <UsageStat
                  icon={Business}
                  label="Bids Submitted (Monthly)"
                  used={usageLimits.bids_submitted}
                  total={usageLimits.bids_remaining === 'unlimited' ? 'unlimited' : 5}
                  color={usageLimits.bids_remaining === 'unlimited' ? '#10B981' : '#2E7D32'}
                />
              </>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Premium Features Preview */}
            {!user?.is_premium && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Premium Features
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography variant="body2">
                      Unlimited problem posting / solution viewing
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography variant="body2">
                      Unlimited bid submissions / comments
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography variant="body2">
                      AI-powered price predictions and market insights
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography variant="body2">
                      Auto-bid generation and submission
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography variant="body2">
                      Priority support and advanced analytics
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Premium Badge for Premium Users */}
            {user?.is_premium && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#F0FDF4', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Verified sx={{ fontSize: 40, color: '#10B981' }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#10B981' }}>
                    Premium Member
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Enjoy unlimited access to all features and priority support
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;